import { computed } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { api } from '@/services/api';
import { AStockData, DailyRecord } from '@/types/sector';

const KEY = ['astock-data'] as const;

/** 拉取全量数据 */
export function useSectorData() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.getAll(),
    staleTime: 30_000,
  });
}

/** 写入（新增/更新） */
export function useUpsertRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (record: DailyRecord) => api.upsert(record),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data.data);
    },
  });
}

/** 删除单日 */
export function useDeleteRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (date: string) => api.deleteDate(date),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data.data);
    },
  });
}

/** 清空全部 */
export function useClearAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.clearAll(),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data.data);
    },
  });
}

/** 导入后失效缓存 */
export function useImportCSV() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.import(file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** 计算总天数 */
export function useTotalDays() {
  const { data } = useSectorData();
  return computed(() => data.value?.dailyData.length ?? 0);
}

/** 数据快照（ref 形式） */
export function useDataSnapshot() {
  const { data } = useSectorData();
  return computed<AStockData>(() => data.value ?? { dailyData: [], sectorScores: {} });
}
